import { Backdrop, CircularProgress } from "@mui/material";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Edit,
  ExternalLink,
  X,
} from "feather-icons-react/build/IconComponents";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API from "../../API/newIndex";
import { NOPHOTO_IMAGE } from "../../Assets/StaticData/StaticImage";
import { GenerateSlugName } from "../../controllers/GenerateSlugName";
import { clientData } from "../../controllers/GetClient";
import ImageUploader from "../../controllers/ImageUploader";
import { useRoutingByType } from "../../controllers/useRouting";
import { uploadTextEditorImage } from "../../utilities/staticFunction";
import AiTextGenerate from "../AiTextGenerate";
import {
  ButtonComponent,
  InputComponent,
  SelectComponent,
  RichTextEditor
} from "@shopeasy-dashboard-ui/ui-library";
import Notification from "../Notification";
import useUnsavedChanges from "../../controllers/useUnsavedChanges";
import UnsavedChangesModal from "../../controllers/UnsavedChangesModal";

const getChangedFields = (currentValues, originalValues) => {
  if (!currentValues || !originalValues) return currentValues || {};
  const changed = {};
  Object.keys(currentValues).forEach((key) => {
    const cur = currentValues[key];
    const orig = originalValues[key];
    if (JSON.stringify(cur) !== JSON.stringify(orig)) {
      changed[key] = cur;
    }
  });
  return changed;
};

const BulkEditBlogCategory = ({
  isBulkEdit = true,
  bulkCategoryIds = [],
  onClose,
  onSuccess,
}) => {
  const [bulkCategoriesList, setBulkCategoriesList] = useState([]);
  const [activeBulkIndex, setActiveBulkIndex] = useState(0);
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const bulkCategoriesDataRef = useRef({});

  const [isNotification, setIsNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState("");
  const [severity, setSeverity] = useState();
  const [btnLoading, setBtnLoading] = useState(false);
  const [formSpinner, setFormSpinner] = useState(false);
  const [AiLoader, setAiloader] = useState(false);

  // Form State & Validation for Active Category Item
  const [warn, setWarn] = useState(false);
  const [enableSlug, setEnableSlug] = useState(false);
  const slugInputRef = useRef(null);

  const [blogCatData, setBlogCatData] = useState({
    blogcategory_name: "",
    meta_tag_title: "",
    meta_tag_description: "",
    keyword: "",
    meta_image: "",
  });
  const [description, setDescription] = useState("");
  const [selectedParentCategory, setSelectedParentCategory] = useState(null);
  const [themeTemplate, setThemeTemplate] = useState("default");
  const [slugName, setSlugName] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [isMetaTitleCustom, setIsMetaTitleCustom] = useState(false);

  const { blogcategory_name, meta_tag_title, meta_tag_description, keyword, meta_image } = blogCatData;

  const originalDataRef = useRef(null);
  const originalPayloadRef = useRef(null);

  const [ParentCategory, setParentCategory] = useState([]);
  const [autocompleteSearch, setAutocompleteSearch] = useState();

  const blogRoute = useRoutingByType("blog");
  const protocol = process.env.REACT_APP_ENVIRONMENT === "DEV" ? "http://" : "https://";
  const clientInfo = clientData;

  const HandleInput = (e) => {
    const { name, value } = e.target;
    if (name === "meta_tag_title") {
      setIsMetaTitleCustom(Boolean(value));
    }
    setBlogCatData((prev) => ({ ...prev, [name]: value }));
  };

  const buildPayload = (formData = {}) => {
    const parentCat = formData.selectedParentCategory;
    const parentId =
      typeof parentCat === "object" && parentCat !== null
        ? parentCat._id || parentCat.id
        : parentCat;

    return {
      blogcategory_name: formData.blogcategory_name || "",
      blog_category_slug_name: formData.slugName || "",
      description: formData.description || "",
      parent_blogcategory: parentId || null,
      theme_template: formData.themeTemplate || "default",
      meta_tag_title: formData.meta_tag_title || "",
      meta_tag_description: formData.meta_tag_description || "",
      keyword: formData.keyword || "",
      meta_image: formData.meta_image || "",
    };
  };

  const currentFormValues = useMemo(() => {
    return {
      blogcategory_name,
      slugName,
      description,
      selectedParentCategory,
      themeTemplate,
      meta_tag_title,
      meta_tag_description,
      keyword,
      meta_image,
    };
  }, [
    blogcategory_name,
    slugName,
    description,
    selectedParentCategory,
    themeTemplate,
    meta_tag_title,
    meta_tag_description,
    keyword,
    meta_image,
  ]);

  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const GetParentCategory = async (searchKey = "") => {
    setIsCategoryLoading(true);
    try {
      const url = `/admin/blogcategory/GetBlogCategoriesWithParent?default=true${searchKey ? `&searchkey=${searchKey}` : ""
        }`;
      const res = await API({ url, method: "get" });
      if (res.status === 200 || res.status === 304) {
        setParentCategory(res?.data?.data?.records || []);
      } else {
        setParentCategory([]);
      }
    } catch (error) {
      console.error("Error fetching parent categories:", error);
      setParentCategory([]);
    } finally {
      setIsCategoryLoading(false);
      setHasSearched(true);
    }
  };

  useEffect(() => {
    GetParentCategory();
  }, []);

  useEffect(() => {
    if (autocompleteSearch && autocompleteSearch.length >= 3) {
      const handler = setTimeout(() => {
        GetParentCategory(autocompleteSearch);
      }, 500);

      return () => {
        clearTimeout(handler);
      };
    } else {
      setIsCategoryLoading(false);
      setHasSearched(false);
    }
  }, [autocompleteSearch]);

  const parentCategoryOptions = useMemo(() => {
    const records = Array.isArray(ParentCategory) ? ParentCategory : (ParentCategory?.data?.records || []);
    return records;
  }, [ParentCategory]);

  const filterOptionsForCategory = (options, state) => {
    const normalizedInputValue = state.inputValue?.toLowerCase();
    if (!normalizedInputValue || normalizedInputValue.length < 3) {
      return [];
    }
    return options.filter((option) => {
      const combinedFields = `${option?.parent_category?.toLowerCase() || ''} ${option?.blogcategory_name?.toLowerCase() || ''}`;
      return combinedFields.includes(normalizedInputValue);
    });
  };

  const parentCategoryValue = useMemo(() => {
    if (!selectedParentCategory) return null;
    if (typeof selectedParentCategory === "object" && selectedParentCategory !== null) {
      return selectedParentCategory;
    }
    const found = parentCategoryOptions.find(
      (cat) => cat._id === selectedParentCategory || cat.id === selectedParentCategory
    );
    if (found) return found;
    return null;
  }, [parentCategoryOptions, selectedParentCategory]);

  useEffect(() => {
    if (parentCategoryOptions && parentCategoryOptions.length > 0 && selectedParentCategory) {
      if (typeof selectedParentCategory === "string") {
        const match = parentCategoryOptions.find((c) => c._id === selectedParentCategory);
        if (match) {
          setSelectedParentCategory(match);
        }
      }
    }
  }, [parentCategoryOptions, selectedParentCategory]);

  const applyCategoryToForm = (rawCategory) => {
    if (!rawCategory) return;
    const parentObj =
      rawCategory?.parentCategory ||
      rawCategory?.parent_category ||
      rawCategory?.parent_blogcategory;

    let initialParentCategory = null;
    if (typeof parentObj === "object" && parentObj !== null) {
      initialParentCategory = parentObj;
    } else if (parentObj) {
      const match = parentCategoryOptions.find((c) => c._id === parentObj);
      initialParentCategory = match || parentObj;
    }

    const initialFormValues = {
      blogcategory_name: rawCategory?.blogcategory_name || rawCategory?.categoryName || "",
      slugName: rawCategory?.blog_category_slug_name || rawCategory?.slug || "",
      description: rawCategory?.description || "",
      selectedParentCategory: initialParentCategory,
      themeTemplate: rawCategory?.theme_template || "default",
      meta_tag_title: rawCategory?.meta_tag_title || "",
      meta_tag_description: rawCategory?.meta_tag_description || "",
      keyword: rawCategory?.keyword || "",
      meta_image: rawCategory?.meta_image || "",
    };

    originalDataRef.current = initialFormValues;
    originalPayloadRef.current = buildPayload(initialFormValues);

    setBlogCatData({
      blogcategory_name: initialFormValues.blogcategory_name,
      meta_tag_title: initialFormValues.meta_tag_title,
      meta_tag_description: initialFormValues.meta_tag_description,
      keyword: initialFormValues.keyword,
      meta_image: initialFormValues.meta_image,
    });
    setIsMetaTitleCustom(Boolean(rawCategory?.meta_tag_title));
    setSlugName(initialFormValues.slugName);
    setSlugEdited(Boolean(initialFormValues.slugName));
    setDescription(initialFormValues.description);
    setSelectedParentCategory(initialFormValues.selectedParentCategory);
    setThemeTemplate(initialFormValues.themeTemplate);
    setEnableSlug(false);
    setWarn(false);
  };

  const fetchCategoriesData = async () => {
    if (!bulkCategoryIds || bulkCategoryIds.length === 0) return;
    setFormSpinner(true);
    try {
      const res = await API({
        url: "/admin/blogcategory/getBlogCategoriesWithIds",
        method: "post",
        data: { categories: bulkCategoryIds, ids: bulkCategoryIds },
      });

      let loadedRecords = [];
      if (res?.status === 200 || res?.status === 304) {
        const resultData = res?.data;
        loadedRecords =
          resultData?.data?.records ||
          resultData?.records ||
          (Array.isArray(resultData?.data) ? resultData.data : []) ||
          (Array.isArray(resultData) ? resultData : []);
      }

      if (loadedRecords.length > 0) {
        setBulkCategoriesList(loadedRecords);
        applyCategoryToForm(loadedRecords[0]);
      } else {
        setIsNotification(true);
        setNotificationMsg("No blog category records found for bulk edit");
        setSeverity("error");
      }
    } catch (error) {
      console.error("Error fetching bulk category data:", error);
      setIsNotification(true);
      setNotificationMsg(error?.message || "Failed to fetch category details");
      setSeverity("error");
    } finally {
      setFormSpinner(false);
    }
  };

  useEffect(() => {
    fetchCategoriesData();
  }, []);

  const switchActiveCategory = (newIndex) => {
    if (newIndex < 0 || newIndex >= bulkCategoriesList.length) return;
    if (newIndex === activeBulkIndex) return;

    const currentCat = bulkCategoriesList[activeBulkIndex];
    if (currentCat) {
      const currentPayload = buildPayload(currentFormValues);
      const baseline = originalPayloadRef.current || originalDataRef.current;
      const changed = baseline ? getChangedFields(currentPayload, baseline) : {};
      const isModified = Object.keys(changed).length > 0;

      bulkCategoriesDataRef.current[currentCat._id] = {
        values: { ...currentFormValues },
        original: originalDataRef.current,
        originalPayload: originalPayloadRef.current,
        isModified,
      };
    }

    setActiveBulkIndex(newIndex);
    const nextCat = bulkCategoriesList[newIndex];
    if (!nextCat) return;

    const saved = bulkCategoriesDataRef.current[nextCat._id];
    if (saved) {
      originalDataRef.current = saved.original;
      originalPayloadRef.current = saved.originalPayload;

      setBlogCatData({
        blogcategory_name: saved.values.blogcategory_name,
        meta_tag_title: saved.values.meta_tag_title,
        meta_tag_description: saved.values.meta_tag_description,
        keyword: saved.values.keyword,
        meta_image: saved.values.meta_image,
      });
      setIsMetaTitleCustom(Boolean(saved ? saved.values.meta_tag_title : nextCat?.meta_tag_title));
      setSlugName(saved.values.slugName);
      setSlugEdited(Boolean(saved.values.slugName));
      setDescription(saved.values.description);
      setSelectedParentCategory(saved.values.selectedParentCategory);
      setThemeTemplate(saved.values.themeTemplate || "default");
      setEnableSlug(false);
      setWarn(false);
    } else {
      applyCategoryToForm(nextCat);
    }
  };

  const isCategoryModified = (cat, index) => {
    if (!cat) return false;
    if (index === activeBulkIndex) {
      const currentPayload = buildPayload(currentFormValues);
      const baseline = originalPayloadRef.current || originalDataRef.current;
      const changed = baseline ? getChangedFields(currentPayload, baseline) : {};
      return Object.keys(changed).length > 0;
    }
    return Boolean(bulkCategoriesDataRef.current[cat._id]?.isModified);
  };

  const modifiedCount = useMemo(() => {
    return bulkCategoriesList.reduce((acc, cat, idx) => {
      return acc + (isCategoryModified(cat, idx) ? 1 : 0);
    }, 0);
  }, [bulkCategoriesList, activeBulkIndex, currentFormValues]);

  const resetAndClose = useCallback(() => {
    bulkCategoriesDataRef.current = {};
    if (onClose) onClose();
  }, [onClose]);

  const {
    showUnsavedModal,
    handleRequestClose: handleBulkEditCancel,
    handleConfirmLeave,
    handleStay,
  } = useUnsavedChanges(modifiedCount > 0, resetAndClose);

  const activeCategoryTitle = useMemo(() => {
    const active = bulkCategoriesList[activeBulkIndex];
    return active?.blogcategory_name || active?.categoryName || "Category";
  }, [bulkCategoriesList, activeBulkIndex]);

  const filteredCategoriesList = useMemo(() => {
    if (!categorySearchTerm || !categorySearchTerm.trim()) return bulkCategoriesList;
    const term = categorySearchTerm.toLowerCase();
    return bulkCategoriesList.filter((cat) => {
      const name = (cat?.blogcategory_name || cat?.categoryName || "").toLowerCase();
      const slug = (cat?.blog_category_slug_name || cat?.slug || "").toLowerCase();
      return name.includes(term) || slug.includes(term);
    });
  }, [bulkCategoriesList, categorySearchTerm]);

  const handleSlug = async (slug) => {
    if (slug?.length) {
      setSlugName(await GenerateSlugName(slug));
    } else {
      setSlugName("");
    }
  };

  const generateMetaData = async (data, key) => {
    if (!description || description.trim().length === 0) {
      setIsNotification(true);
      setNotificationMsg("Please enter description!");
      setSeverity("error");
      return;
    }
    try {
      setAiloader(true);
      const payload = { name: data };
      if (key === "description") {
        const ApiCall = await API({
          url: "/admin/ai/category_meta_description",
          method: "post",
          data: payload,
        });
        setBlogCatData((prev) => ({
          ...prev,
          meta_tag_description: ApiCall?.data?.data || "",
        }));
      } else {
        const ApiCall = await API({
          url: "/admin/ai/category_meta_title",
          method: "post",
          data: payload,
        });
        setIsMetaTitleCustom(true);
        setBlogCatData((prev) => ({
          ...prev,
          meta_tag_title: ApiCall?.data?.data || "",
        }));
      }
    } catch (error) {
      if (error?.response?.status === 429) {
        setIsNotification(true);
        setNotificationMsg(t("lang_Hit_chatgpt_limit") || "ChatGPT limit reached");
        setSeverity("warning");
      } else {
        setIsNotification(true);
        setNotificationMsg(error?.response?.data?.message || "Failed to generate metadata");
        setSeverity("error");
      }
    } finally {
      setAiloader(false);
    }
  };

  const handleBulkUpdate = async () => {
    // Validate current item
    if (!blogcategory_name || !blogcategory_name.trim()) {
      setWarn(true);
      setIsNotification(true);
      setNotificationMsg("Blog Category Name is required!");
      setSeverity("error");
      return;
    }

    try {
      setBtnLoading(true);
      setFormSpinner(true);

      const currentCat = bulkCategoriesList[activeBulkIndex];
      if (currentCat) {
        const currentPayload = buildPayload(currentFormValues);
        const baseline = originalPayloadRef.current || originalDataRef.current;
        const changed = baseline ? getChangedFields(currentPayload, baseline) : {};
        const isModified = Object.keys(changed).length > 0;

        bulkCategoriesDataRef.current[currentCat._id] = {
          values: { ...currentFormValues },
          original: originalDataRef.current,
          originalPayload: originalPayloadRef.current,
          isModified,
        };
      }

      const modifiedEntries = Object.entries(bulkCategoriesDataRef.current).filter(
        ([_, data]) => data?.isModified
      );

      if (modifiedEntries.length === 0) {
        setIsNotification(true);
        setNotificationMsg("No changes detected to update");
        setSeverity("info");
        setBtnLoading(false);
        setFormSpinner(false);
        return;
      }

      // Check validation for all modified items
      for (const [catId, data] of modifiedEntries) {
        if (!data.values.blogcategory_name || !data.values.blogcategory_name.trim()) {
          setIsNotification(true);
          setNotificationMsg("All modified categories must have a Category Name");
          setSeverity("error");
          setBtnLoading(false);
          setFormSpinner(false);
          return;
        }
      }

      const bulkPayload = modifiedEntries
        .map(([catId, data]) => {
          const payload = buildPayload(data.values);
          const baseline = data.originalPayload || data.original;
          const changed = getChangedFields(payload, baseline);

          const fieldsToUpdate = changed && Object.keys(changed).length > 0 ? changed : payload;

          return {
            _id: catId,
            id: catId,
            data: fieldsToUpdate,
            ...fieldsToUpdate,
          };
        })
        .filter(Boolean);

      if (bulkPayload.length === 0) {
        setIsNotification(true);
        setNotificationMsg("No changes detected to update");
        setSeverity("info");
        setBtnLoading(false);
        setFormSpinner(false);
        return;
      }

      const res = await API({
        url: "/admin/blogcategory/bulkUpdateBlogCategories",
        method: "post",
        data: bulkPayload,
      });

      if (res.status === 200 || res.status === 304) {
        setIsNotification(true);
        setNotificationMsg(
          `${bulkPayload.length} categor${bulkPayload.length > 1 ? "ies" : "y"} updated successfully!`
        );
        setSeverity("success");
        bulkCategoriesDataRef.current = {};
        if (onSuccess) {
          onSuccess(bulkPayload.length);
        }
      } else {
        throw new Error(res?.data?.message || "Failed to update blog categories");
      }
    } catch (error) {
      console.error("Error bulk updating categories:", error);
      setIsNotification(true);
      setNotificationMsg(error?.message || error || "Failed to update blog categories");
      setSeverity("error");
    } finally {
      setBtnLoading(false);
      setFormSpinner(false);
    }
  };

  const handleClose = () => {
    setIsNotification(false);
    setNotificationMsg("");
    setSeverity("");
  };

  const wordCount = (description || "")
    .replace(/<[^>]*>/g, " ")
    .split(/\s+/)
    .filter(Boolean).length;

  const metaDescLength = (meta_tag_description || "").replace(/<[^>]+>/g, "").length;

  return (
    <div className="flex flex-col h-screen w-screen overflow-hidden bg-gray-100 dark:bg-zinc-950 text-gray-900 dark:text-zinc-100">
      {isNotification && notificationMsg && (
        <Notification message={notificationMsg} close={handleClose} severity={severity} />
      )}
      {(formSpinner || AiLoader) && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={formSpinner || AiLoader}
          className="flex flex-col"
        >
          <CircularProgress color="inherit" />
          <p className="mt-2 text-sm">
            {AiLoader ? t("lang_ai_generate_content_loader") || "Generating content..." : t("lang_loading") || "Processing..."}
          </p>
        </Backdrop>
      )}

      {/* Top Header Bar */}
      <div className="flex items-center justify-between px-6 py-3.5 border-b border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shrink-0">
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={handleBulkEditCancel}
            className="w-7 h-7 rounded-full flex items-center justify-center text-gray-500 dark:text-zinc-400 hover:bg-gray-100 dark:hover:bg-zinc-800 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <h5 className="text-sm font-bold text-gray-900 dark:text-white m-0">
                Bulk Edit Blog Categories
              </h5>
              <span className="bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-zinc-300 text-[11px] font-semibold px-2 py-0.5 rounded-full">
                {bulkCategoriesList.length} Selected
              </span>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-zinc-400 m-0 mt-0.5">
              Select any category from the list to view and edit its details.
            </p>
          </div>
        </div>
      </div>

      {/* Main Body */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left Sidebar Panel */}
        <div className="w-[300px] border-r border-gray-200 dark:border-zinc-800 bg-gray-50 dark:bg-zinc-950 p-4 flex flex-col shrink-0">
          {/* Outer Rounded Container matching Reference */}
          <div className="flex-1 flex flex-col border border-gray-200 dark:border-zinc-800 rounded-2xl bg-white dark:bg-zinc-900 overflow-hidden shadow-xs">
            {/* Header section */}
            <div className="p-3.5 border-b border-gray-200 dark:border-zinc-800 flex items-center justify-between">
              <span className="text-xs font-bold text-gray-900 dark:text-white">
                Selected Categories
              </span>
              <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-zinc-400">
                <button
                  type="button"
                  disabled={activeBulkIndex === 0}
                  onClick={() => switchActiveCategory(activeBulkIndex - 1)}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 disabled:opacity-30 disabled:pointer-events-none cursor-pointer text-gray-600 dark:text-zinc-400"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="font-mono text-[11px] text-gray-600 dark:text-zinc-400">
                  {activeBulkIndex + 1}/{bulkCategoriesList.length}
                </span>
                <button
                  type="button"
                  disabled={activeBulkIndex === bulkCategoriesList.length - 1}
                  onClick={() => switchActiveCategory(activeBulkIndex + 1)}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 disabled:opacity-30 disabled:pointer-events-none cursor-pointer text-gray-600 dark:text-zinc-400"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>

            {/* Filter Search Input Section */}
            <div className="p-3 border-b border-gray-200 dark:border-zinc-800 bg-transparent">
              <input
                type="text"
                placeholder="Filter selected categories..."
                value={categorySearchTerm}
                onChange={(e) => setCategorySearchTerm(e.target.value)}
                className="w-full px-3 py-1.5 text-xs border border-gray-200 dark:border-zinc-700/80 rounded-lg bg-white dark:bg-zinc-800/80 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-zinc-400 focus:outline-none shadow-xs"
              />
            </div>

            {/* Items List flush inside container with clean dark borders */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {filteredCategoriesList.map((cat) => {
                const actualIndex = bulkCategoriesList.findIndex((item) => item._id === cat._id);
                const isActive = actualIndex === activeBulkIndex;
                const isModified = isCategoryModified(cat, actualIndex);

                return (
                  <div
                    key={cat._id}
                    onClick={() => switchActiveCategory(actualIndex)}
                    className={`p-3 cursor-pointer transition-colors flex items-center justify-between gap-3 border-b border-gray-200 dark:border-zinc-800 last:border-b-0 ${isActive
                      ? "bg-gray-100 dark:bg-zinc-800/90"
                      : "bg-white dark:bg-zinc-900 hover:bg-gray-100 dark:hover:bg-zinc-800/60"
                      }`}
                  >
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-md bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700/80 flex items-center justify-center shrink-0 overflow-hidden p-1">
                        <img
                          src={cat?.meta_image || cat?.image || NOPHOTO_IMAGE}
                          onError={(e) => {
                            e.target.src = NOPHOTO_IMAGE;
                          }}
                          className="w-full h-full object-contain"
                          alt=""
                        />
                      </div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className={`text-xs truncate ${isActive ? "font-bold text-gray-900 dark:text-white" : "font-medium text-gray-800 dark:text-zinc-200"}`}>
                          {cat?.blogcategory_name || cat?.categoryName || "-"}
                        </span>
                        <span className="text-[11px] text-gray-400 dark:text-zinc-400 truncate mt-0.5">
                          Category
                        </span>
                      </div>
                    </div>

                    {isModified && (
                      <span
                        title='Modified'
                        className={`w-2 h-2 rounded-full bg-amber-500 shrink-0 select-none inline-block`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Main Form Content Area - Single Stack Layout matching AddBCategory.js */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden bg-gray-100 dark:bg-zinc-950">
          {/* Right Sub-Header Bar */}
          <div className="flex items-center justify-between px-6 py-2.5 bg-white dark:bg-zinc-900 border-b border-gray-200 dark:border-zinc-800 text-xs shrink-0">
            <div>
              <span className="font-bold text-gray-900 dark:text-white">
                Currently Editing: {activeCategoryTitle}
              </span>{" "}
              <span className="text-gray-400 dark:text-zinc-500 font-mono text-[11px]">
                ({activeBulkIndex + 1} of {bulkCategoriesList.length})
              </span>
            </div>
            <div>
              {modifiedCount > 0 ? (
                <span className="text-emerald-600 dark:text-emerald-400 font-medium text-xs">
                  {modifiedCount} category modified
                </span>
              ) : (
                <span className="text-gray-400 dark:text-zinc-500 text-xs font-normal">No unsaved changes</span>
              )}
            </div>
          </div>

          {/* Form Area matching AddBCategory.js field sequence & card stack */}
          <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
            <form
              onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
              className="max-w-4xl mx-auto space-y-5"
            >
              {/* Card 1: Main Details (Category Name * -> Parent Category -> Description) */}
              <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-2xl p-6 shadow-xs space-y-4">
                {/* Category Name * */}
                <div>
                  <label htmlFor="blogcategory_name">
                    <p className="text-xs font-semibold text-gray-700 dark:text-zinc-300 mb-1">
                      {t("lang_Blog_Category_Name") || "Blog Category Name"} <span className="text-red-500">*</span>
                    </p>
                  </label>
                  <InputComponent
                    className={warn ? "border-red-500" : ""}
                    type="text"
                    id="blogcategory_name"
                    name="blogcategory_name"
                    value={blogcategory_name}
                    onChange={(e) => {
                      const newName = e.target.value;
                      setWarn(false);
                      if (!enableSlug && (!slugEdited || !slugName)) handleSlug(newName);
                      const shouldSyncMeta = !isMetaTitleCustom;
                      setBlogCatData((prev) => ({
                        ...prev,
                        blogcategory_name: newName,
                        ...(shouldSyncMeta ? { meta_tag_title: newName } : {}),
                      }));
                    }}
                    onBlur={() => {
                      if (!enableSlug && (!slugEdited || !slugName) && blogcategory_name) handleSlug(blogcategory_name);
                      if (!isMetaTitleCustom && !meta_tag_title && blogcategory_name) {
                        setBlogCatData((prev) => ({ ...prev, meta_tag_title: blogcategory_name }));
                      }
                    }}
                    placeholder="e.g. Tech"
                  />

                  {/* Slug Inline Edit & Preview Bar */}
                  <div className="flex items-center justify-between mt-1 text-xs text-gray-500 dark:text-zinc-400 w-full gap-1 min-h-[24px]">
                    <div className="flex items-center w-[90%] min-w-0">
                      <a
                        href={`${protocol}${clientInfo?.custom_domain || clientInfo?.customer_domain}${blogRoute}/${(slugName || "").toLowerCase()}`}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium shrink-0 mr-2 hover:underline cursor-pointer text-blue-600 dark:text-blue-400 flex items-center text-[11px]"
                      >
                        Preview
                        <ExternalLink size={12} className="ml-0.5" />
                      </a>
                      {!enableSlug ? (
                        <div className="flex items-center gap-2 min-w-0 text-[11px]">
                          <span className="truncate text-gray-500 dark:text-zinc-400 min-w-0">
                            {`${protocol}${clientInfo?.custom_domain || clientInfo?.customer_domain}${blogRoute}/${(slugName || "").toLowerCase()}`}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setEnableSlug(true);
                              setTimeout(() => slugInputRef.current?.focus(), 50);
                            }}
                            className="text-gray-600 hover:text-black dark:text-zinc-400 dark:hover:text-white shrink-0 flex items-center cursor-pointer"
                          >
                            <Edit size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 min-w-0 flex-1 text-[11px]">
                          <span className="text-gray-400 dark:text-zinc-500 shrink truncate max-w-[120px] sm:max-w-[200px]">
                            {`${protocol}${clientInfo?.custom_domain || clientInfo?.customer_domain}${blogRoute}/`}
                          </span>
                          <InputComponent
                            ref={slugInputRef}
                            name="blog_category_slug_name"
                            className="flex-1 min-w-[60px] !h-[22px] !py-0.5 !px-1 text-xs"
                            value={slugName || ""}
                            onChange={(e) => {
                              setSlugName(e.target.value);
                              setSlugEdited(Boolean(e.target.value));
                            }}
                            onBlur={async () => {
                              if (slugName) {
                                const generated = await GenerateSlugName(slugName);
                                setSlugName(generated);
                              }
                            }}
                            onKeyDown={async (e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                if (!slugName) {
                                  setIsNotification(true);
                                  setSeverity("warning");
                                  setNotificationMsg("Slug cannot be empty!");
                                  return;
                                }
                                const generated = await GenerateSlugName(slugName);
                                setSlugName(generated);
                                setEnableSlug(false);
                                setSlugEdited(true);
                              }
                            }}
                            isSlug={true}
                          />
                          <button
                            type="button"
                            className="text-green-600 dark:text-green-400 ml-1 shrink-0 flex items-center cursor-pointer"
                            onClick={async () => {
                              if (!slugName) {
                                setIsNotification(true);
                                setSeverity("warning");
                                setNotificationMsg("Slug cannot be empty!");
                                return;
                              }
                              const generated = await GenerateSlugName(slugName);
                              setSlugName(generated);
                              setEnableSlug(false);
                              setSlugEdited(true);
                            }}
                          >
                            <Check size={14} />
                          </button>
                        </div>
                      )}
                    </div>

                    <span
                      className={`shrink-0 text-end text-[11px] ${(blogcategory_name?.length || 0) > 60
                        ? "text-red-500 font-semibold"
                        : "text-gray-400 dark:text-zinc-500"
                        }`}
                    >
                      {blogcategory_name?.length || 0}/60
                    </span>
                  </div>

                  {warn && (
                    <p className="text-red-500 text-xs mt-1">Please enter Category Name</p>
                  )}
                </div>

                {/* Parent Blog Category matching AddBCategory.js */}
                <div>
                  <label
                    htmlFor="parent_blogcategory"
                    className="text-xs font-semibold text-gray-700 dark:text-zinc-300 mb-1 block"
                  >
                    {t("lang_Parent_Blog_Category") || "Parent Blog Category"}
                  </label>
                  <SelectComponent
                    className="autocomplete"
                    options={parentCategoryOptions}
                    value={parentCategoryValue}
                    getOptionLabel={(option) => {
                      if (!option) return "";
                      if (typeof option === "string") return option;
                      return (
                        option?.blogcategory_name ||
                        option?.categoryName ||
                        (typeof option?.parent_category === "string"
                          ? option.parent_category.split(">").pop().trim()
                          : "") ||
                        ""
                      );
                    }}
                    getOptionValue={(option) => option?._id || option?.id}
                    filterOptions={filterOptionsForCategory}
                    onInputChange={(newInputValue) => {
                      if (typeof newInputValue === "string" && newInputValue?.length >= 3) {
                        setHasSearched(false);
                        setAutocompleteSearch(newInputValue);
                      } else {
                        setIsCategoryLoading(false);
                        setHasSearched(false);
                        setAutocompleteSearch("");
                      }
                    }}
                    noOptionsText={
                      isCategoryLoading
                        ? `${t("lang_loading") || "Loading"}...`
                        : !autocompleteSearch || autocompleteSearch.length < 3 || !hasSearched
                          ? t("lang_enter_three_characters") || "Please enter at least 3 characters to search"
                          : t("lang_no_option_found") || "No options found"
                    }
                    onChange={(e, v) => {
                      const selectedCat =
                        typeof v === "object" && v !== null
                          ? v
                          : parentCategoryOptions?.find((cat) => cat._id === (e?.target?.value || v));
                      setSelectedParentCategory(selectedCat ? selectedCat : null);
                    }}
                    placeholder={`${t("lang_select") || "Select"} ${t("lang_parent_category") || "parent category"}`}
                    isClearable={true}
                    tabIndex={2}
                  />
                </div>

                {/* Blog Category Description */}
                <div>
                  <label
                    htmlFor="description"
                    className="text-xs font-semibold text-gray-700 dark:text-zinc-300 mb-1 block"
                  >
                    {t("lang_Blog_Category_Description") || "Blog Category Description"}
                  </label>
                  <RichTextEditor
                    htmlData={description}
                    setHtmlData={(val) => setDescription(val)}
                    uplaodImageFile={uploadTextEditorImage}
                  />
                  <div className="flex w-full justify-end mt-1">
                    <p
                      className={`text-[11px] ${wordCount > 280 ? "text-red-500 font-semibold" : "text-gray-400 dark:text-zinc-500"
                        }`}
                    >
                      {wordCount}/280 words
                    </p>
                  </div>
                </div>
              </div>

              {/* Card 2: Search Engine Listing */}
              <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-2xl p-6 shadow-xs space-y-4">
                <h6 className="text-xs font-bold text-gray-900 dark:text-white m-0">
                  {t("lang_search_engine_listing") || "Search Engine Listing"}
                </h6>

                {/* Live Google Snippet Preview Box */}
                <div className="w-full">
                  {meta_tag_title?.length > 0 ? (
                    <div className="w-full bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-xl shadow-xs p-4 text-left flex gap-4 items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-[#1a0dab] dark:text-blue-400 text-sm font-medium leading-snug hover:underline cursor-pointer truncate">
                          {meta_tag_title}
                        </p>
                        <div className="flex items-center text-xs text-[#006621] dark:text-emerald-400 mt-1 truncate">
                          {`${protocol}${clientInfo?.custom_domain || clientInfo?.customer_domain}${blogRoute}/${(slugName || "").toLowerCase()}`}
                        </div>
                        <p
                          className="text-[#4d5156] dark:text-zinc-300 text-xs mt-1 leading-relaxed line-clamp-3"
                          dangerouslySetInnerHTML={{ __html: meta_tag_description }}
                        ></p>
                      </div>
                      {meta_image && (
                        <div className="w-24 h-16 shrink-0 rounded-lg overflow-hidden border border-gray-100 dark:border-zinc-700 flex items-center justify-center bg-gray-50 dark:bg-zinc-900">
                          <img src={meta_image} className="w-full h-full object-contain" alt="Preview" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="w-full bg-gray-50 dark:bg-zinc-800/50 border border-gray-200 dark:border-zinc-700 rounded-xl p-4 text-left flex gap-4 items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-400 dark:text-zinc-400 text-sm font-medium leading-snug truncate">
                          Example Blog Category Title
                        </p>
                        <div className="flex items-center text-xs text-gray-400 dark:text-zinc-500 mt-1 truncate">
                          yourdomain.com/blog/sample-category
                        </div>
                        <p className="text-gray-400 dark:text-zinc-400 text-xs mt-1 leading-relaxed line-clamp-2">
                          This is a sample meta description. When you add a title and description, your search engine preview will appear here.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <AiTextGenerate
                    fieldLabel={t("lang_meta_title") || "Meta Title"}
                    label={t("lang_get_meta_title") || "Generate"}
                    tooltip={t("lang_meta_tooltip", { type: "title" })}
                    onGenerate={() => generateMetaData(description, "title")}
                  />
                  <InputComponent
                    type="text"
                    id="meta_tag_title"
                    name="meta_tag_title"
                    value={meta_tag_title}
                    onChange={HandleInput}
                    placeholder="Enter meta title"
                  />
                  <div className="flex w-full justify-end mt-1">
                    <p
                      className={`text-[11px] ${(meta_tag_title?.length || 0) > 60
                        ? "text-red-500 font-semibold"
                        : "text-gray-400 dark:text-zinc-500"
                        }`}
                    >
                      {meta_tag_title?.length || 0}/60
                    </p>
                  </div>
                </div>

                <div>
                  <AiTextGenerate
                    fieldLabel={t("lang_meta_description") || "Meta Description"}
                    label={t("lang_get_meta_description") || "Generate"}
                    tooltip={t("lang_meta_tooltip", { type: "description" })}
                    onGenerate={() => generateMetaData(description, "description")}
                  />
                  <textarea
                    className="w-full h-24 block px-3 py-2 border border-gray-200 dark:border-zinc-700 rounded-lg focus:outline-none focus:border-gray-400 dark:focus:border-zinc-500 bg-white dark:bg-zinc-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-zinc-500 text-xs"
                    id="meta_tag_description"
                    name="meta_tag_description"
                    value={(meta_tag_description || "").replace(/<[^>]+>/g, "")}
                    onChange={HandleInput}
                    placeholder="Enter meta description"
                  />
                  <div className="flex w-full justify-end mt-1">
                    <p
                      className={`text-[11px] ${metaDescLength > 160 ? "text-red-500 font-semibold" : "text-gray-400 dark:text-zinc-500"
                        }`}
                    >
                      {metaDescLength}/160 words
                    </p>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="keyword"
                    className="text-xs font-semibold text-gray-700 dark:text-zinc-300 mb-1 block"
                  >
                    {t("lang_meta_keywords") || "Meta Keywords"}
                  </label>
                  <InputComponent
                    type="text"
                    id="keyword"
                    name="keyword"
                    value={keyword}
                    onChange={HandleInput}
                    placeholder="e.g. Cloth fashion"
                  />
                </div>

                <div>
                  <label htmlFor="meta_image">
                    <p className="text-xs font-semibold text-gray-700 dark:text-zinc-300 mb-1">
                      {t("lang_meta_image") || "Meta Image"}
                    </p>
                  </label>
                  <ImageUploader
                    alreadyImage={meta_image}
                    handleSetImage={(value) =>
                      setBlogCatData((prev) => ({ ...prev, meta_image: value }))
                    }
                    folder="og"
                    format="image"
                    removeImages={() =>
                      setBlogCatData((prev) => ({ ...prev, meta_image: "" }))
                    }
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Sticky Bottom Action Bar */}
      <div className="flex items-center justify-between w-full border-t border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-3.5 px-6 shrink-0">
        <button
          type="button"
          onClick={handleBulkEditCancel}
          className="flex items-center text-xs font-medium text-gray-500 dark:text-zinc-400 hover:text-gray-900 dark:hover:text-white transition-colors gap-1 cursor-pointer"
        >
          ← cancel
        </button>

        <div className="flex items-center gap-2">
          <ButtonComponent
            variant='primary'
            size='sm'
            loading={btnLoading}
            disabled={modifiedCount === 0 || btnLoading}
            onClick={handleBulkUpdate}
          >
            {modifiedCount > 0 ? `Save Changes (${modifiedCount})` : 'Save Changes'}
          </ButtonComponent>
        </div>
      </div>

      <UnsavedChangesModal
        open={showUnsavedModal}
        onStay={handleStay}
        onLeave={handleConfirmLeave}
      />
    </div>
  );
};

export default BulkEditBlogCategory;
